<script setup lang="ts">
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {computed, ref, watch} from "vue";
import {TeamRole, UserSummary} from "@/types";
import axios from "axios";

interface Props {
    teamId: number;
    isOwner?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isOwner: false,
})
const value = defineModel<boolean>()

const form = useForm({
    users: [],
    role: 'user' as TeamRole,
})

const search = ref<string>('')
const items = ref<Array<UserSummary>>([])

const options: Array<{ label: string; value: TeamRole }> = [
    {label: 'Owner', value: 'owner'},
    {label: 'Admin', value: 'admin'},
    {label: 'User', value: 'user'},
    {label: 'Guest', value: 'guest'}
]

const filteredOptions = computed(() => {
    if (props.isOwner) {
        return options;
    } else {
        return options.filter(option => option.value !== 'owner');
    }
})

const submit = () => {
    form.post(route('teams.users.store', {team: props.teamId}), {
        preserveState: true,
        onSuccess: () => {
            value.value = false;
            search.value = '';
            form.reset()
        },
    })
}

const loadItems = async (search: string) => {
    try {
        const { data } = await axios.get(route('teams.not-members', { team: props.teamId }), {
            params: { search }
        });

        items.value = data;
    } catch (error) {
        items.value = [];
    }
};

const closeDialog = () => {
    value.value = false;
    search.value = '';
    form.reset();
}

watch(search, (newSearch) => {
    loadItems(newSearch);
}, {immediate: true});
</script>

<template>
    <v-dialog
        v-model="value"
        max-width="500"
        scrim="blue-grey-darken-4"
        opacity="0.5"
        transition="dialog-top-transition"
    >
        <template #default>
            <v-card
                class="pa-1"
                rounded="lg"
            >
                <v-form @submit.prevent="submit">
                    <v-card-title class="font-weight-bold">
                        Invite members
                        <v-btn
                            :icon="true"
                            color="base"
                            variant="plain"
                            rounded="lg"
                            class="float-end"
                            size="32"
                            @click="closeDialog"
                        >
                            <v-icon size="20">
                                mdi-close
                            </v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="text-body-2 px-4 py-0">
                        <p class="mb-5">
                            You're inviting members to join this resource.
                        </p>
                        <v-alert
                            v-if="form.errors.users"
                            variant="tonal"
                            type="error"
                            icon="mdi-alert-circle-outline"
                            icon-size="20"
                            class="text-body-2 mt-5 mb-2"
                        >
                            <span class="text-black">
                                {{ form.errors.users }}
                            </span>
                        </v-alert>
                        <v-field-label>
                            Name or email address
                        </v-field-label>
                        <v-autocomplete
                            v-model="form.users"
                            v-model:search="search"
                            variant="outlined"
                            :items="items"
                            multiple
                            density="compact"
                            return-object
                            hide-details
                            no-filter
                            item-title="name"
                            item-value="id"
                            clear-on-select
                            hide-selected
                            no-data-text="No users found"
                            :list-props="{
                                class: 'py-0'
                            }"
                            :menu-props="{
                                location: 'bottom'
                            }"
                            menu-icon="null"
                        >
                            <template #chip="{ item, props: binds}">
                                <v-chip
                                    v-bind="binds"
                                    class="text-body-2"
                                    closable
                                    tile
                                    close-icon="mdi-close"
                                    @click:close="binds.onClick as Event"
                                >
                                    {{ item.raw.name }}
                                </v-chip>
                            </template>
                            <template #item="{ item, props: binds }">
                                <v-list-item
                                    :value="binds.value"
                                    link
                                    @click="binds.onClick as Event"
                                >
                                    <v-list-item-title
                                        class="text-body-2 font-weight-bold"
                                    >
                                        {{ item.raw.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle
                                        class="text-body-2 text-medium-emphasis"
                                    >
                                        {{ item.raw.email }}
                                    </v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <div class="text-body-2 text-medium-emphasis mb-3 mt-1">
                            Select the members you want to invite to this resource. Learn more about <a
                                href="https://clinbiokb.sib.swiss/s/capacity-framework/doc/manage-resource-access-and-roles-a3p8a2BStX"
                                target="_blank"
                                class="text-decoration-none text-primary"
                            >roles</a>.
                        </div>
                        <v-field-label class="mt-3">
                            Select role
                        </v-field-label>
                        <v-select
                            v-model="form.role"
                            :items="filteredOptions"
                            item-title="label"
                            item-value="value"
                            variant="outlined"
                            density="compact"
                            :hide-details="!form.errors.role"
                            :error-messages="form.errors.role"
                            :menu-props="{
                                location: 'bottom',
                            }"
                            :list-props="{
                                class: 'py-0'
                            }"
                            menu-icon="mdi-chevron-down"
                            max-width="250"
                        />
                        <div class="text-body-2 text-medium-emphasis mb-3 mt-1">
                            Invited members will be assigned the selected role.
                        </div>
                    </v-card-text>
                    <v-card-actions class="pt-0 mt-5">
                        <v-spacer />
                        <v-btn
                            color="white"
                            variant="flat"
                            border="sm"
                            type="button"
                            @click="closeDialog"
                        >
                            <span class="font-weight-regular">
                                Cancel
                            </span>
                        </v-btn>
                        <v-btn
                            variant="flat"
                            border="sm"
                            type="submit"
                        >
                            <span class="font-weight-regular">
                                Invite
                            </span>
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </template>
    </v-dialog>
</template>

<style scoped>

</style>
